import Extension from "../base/extension";

class Nav extends Extension {
  #section;

  // The document position of the bottom of the header's LAYOUT slot — the point
  // past which there is finally page content behind the header. See
  // #trackFlowBottom.
  #flowBottom = 0;

  constructor(name) {
    super(name);
  }

  init() {
    this.#section = document.querySelector('.section-header');
    this.#seedFlowBottom();
    this.#publishHeaderHeight();
    this.setupScrollListener();
    this.setupHoverIntent();
    this.setupMegamenuKeyboard();
    this.setupSkipLink();
    this.setupOffcanvasPanels();
    document.addEventListener('interaction:down-scroll', this.onScrollDown.bind(this));
    document.addEventListener('interaction:up-scroll', this.onScrollUp.bind(this));
  }

  // Publish the header's rendered height as CSS custom properties so other
  // sections can offset sticky content beneath it. `--header-height` is the
  // constant height; `--header-offset` tracks the *currently visible* height
  // (set to 0 while the header is hidden on down-scroll). A ResizeObserver keeps
  // both in sync when the header reflows (e.g. responsive breakpoints, USP bar).
  #publishHeaderHeight() {
    if (!this.#section) return;
    const update = () => {
      const h = `${this.#section.offsetHeight}px`;
      document.documentElement.style.setProperty('--header-height', h);
      document.documentElement.style.setProperty('--header-offset', h);
      this.#publishHeaderBottom();
    };
    update();
    new ResizeObserver(update).observe(this.#section);
  }

  // Where the header's bottom edge actually is in the viewport, for anything
  // pinned beneath it in the TOP LAYER — which is positioned against the
  // viewport, not against the header. `--header-offset` is the header's HEIGHT,
  // so it only doubles as its bottom edge while the header is flush with the
  // top of the screen; put a notification bar above it and everything keyed to
  // the offset rides up over the nav by the height of that bar. Recomputed on
  // scroll, because the gap closes as the bar scrolls away and the sticky
  // header parks at 0.
  #publishHeaderBottom() {
    if (!this.#section) return;
    document.documentElement.style.setProperty(
      '--header-bottom',
      `${Math.max(0, this.#section.getBoundingClientRect().bottom)}px`
    );
  }

  // Where the header's LAYOUT slot ends, in document coordinates.
  //
  // The autohide ($navbar-autohide, core.scss) fades the header to opacity 0,
  // but the header is `position: sticky` — its slot in the flow stays exactly
  // where it was. So while that slot is still on screen, the thing BEHIND the
  // hidden header is the slot itself: nothing. Hiding there paints a band of
  // bare page background where the header should be. Past the slot, real page
  // content has scrolled up underneath and hiding does what it means to do.
  //
  // Only measurable while the header is NOT stuck: once it pins, `rect.top` is
  // 0 by definition, and `offsetTop` follows the sticky offset too (measured in
  // Chrome: 400 at scrollY 400), so neither can be read after the fact. Keep the
  // last honest reading instead.
  #trackFlowBottom() {
    if (!this.#section) return;
    const rect = this.#section.getBoundingClientRect();
    if (rect.top > 0) {
      this.#flowBottom = rect.bottom + window.scrollY;
    }
  }

  // A page can load already scrolled (a #anchor, a restored position), with the
  // header pinned before any scroll event fires and no honest reading available.
  // Seed from wherever it currently sits: while pinned that OVER-estimates the
  // slot by however far the page has scrolled, which errs towards keeping the
  // header visible — the safe direction. The first scroll back to the top
  // replaces it with a true value.
  #seedFlowBottom() {
    if (!this.#section) return;
    this.#flowBottom = this.#section.getBoundingClientRect().bottom + window.scrollY;
  }

  onScrollDown(e) {
    this.#trackFlowBottom();
    // Never hide over the header's own empty slot (see #trackFlowBottom).
    if(e.detail.difference > 5 && window.scrollY >= this.#flowBottom) {
      this.#section.classList.remove('scrolled-up');
      this.#section.classList.add('scrolled-down');
      document.documentElement.style.setProperty('--header-offset', '0px');
    }
  }

  onScrollUp(e) {
    // Scrolling up is how the header's slot comes back into view, so this is
    // where the honest reading of #flowBottom is usually taken.
    this.#trackFlowBottom();
    if(e.detail.difference > 5) {
      this.#section.classList.remove('scrolled-down');
      this.#section.classList.add('scrolled-up');
      document.documentElement.style.setProperty(
        '--header-offset',
        `${this.#section.offsetHeight}px`
      );
    }
  }

  // Hover-intent for the desktop megamenus: only open a panel once the pointer
  // has rested on the nav item for HOVER_OPEN_DELAY_MS, so a quick scroll-pass
  // doesn't trigger it. Closing stays instant (cleared on mouseleave). Keyboard
  // opening is handled separately by setupMegamenuKeyboard, so it isn't delayed.
  setupHoverIntent() {
    const HOVER_OPEN_DELAY_MS = 200;
    const dropdownItems = document.querySelectorAll('.dropdown-hover');

    dropdownItems.forEach((dropdownItem) => {
      let hoverOpenTimer = null;

      dropdownItem.addEventListener('mouseenter', () => {
        hoverOpenTimer = setTimeout(() => {
          dropdownItem.classList.add('dropdown-hover-open');
        }, HOVER_OPEN_DELAY_MS);
      });

      dropdownItem.addEventListener('mouseleave', () => {
        clearTimeout(hoverOpenTimer);
        dropdownItem.classList.remove('dropdown-hover-open');
      });
    });
  }

  // Keyboard disclosure for the desktop dropdowns and megamenus, with no visible
  // control:
  // the top-level nav link itself is the (invisible) disclosure. Closed panels
  // are display:none (never :focus-within-revealed), so crossing the header by
  // keyboard is one tab stop per menu instead of hundreds of panel links.
  // ArrowDown opens the panel (`mp-open` + aria-expanded) and moves focus into
  // it; Enter still follows the link; Escape closes and returns focus; tabbing
  // out closes. Scoped to `.dropdown-hover` items, so the remaining Bootstrap
  // dropdowns (account / locale / facets-inline) are untouched: those still
  // bring their own keyboard handling until they migrate too.
  setupMegamenuKeyboard() {
    document.querySelectorAll('.dropdown-hover').forEach((dropdown) => {
      // `.megamenu` first so a megamenu's own panel wins, then plain
      // `.dropdown-menu`: header style A's child-link menus are ordinary
      // dropdowns with no megamenu panel, and without this they would have lost
      // keyboard opening entirely when they came off Bootstrap's Dropdown.
      const panel = dropdown.querySelector('.megamenu, .dropdown-menu');
      const link = dropdown.querySelector('.nav-link');
      if (!panel || !link) return;

      const open = () => {
        dropdown.classList.add('mp-open');
        link.setAttribute('aria-expanded', 'true');
      };
      const close = (returnFocus) => {
        dropdown.classList.remove('mp-open');
        link.setAttribute('aria-expanded', 'false');
        if (returnFocus) link.focus();
      };

      link.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          open();
          const first = panel.querySelector('a, button');
          if (first) first.focus();
        }
      });

      dropdown.addEventListener('keydown', (event) => {
        if ((event.key === 'Escape' || event.key === 'Esc') && dropdown.classList.contains('mp-open')) {
          event.stopPropagation();
          close(true);
        }
      });

      dropdown.addEventListener('focusout', (event) => {
        if (!dropdown.contains(event.relatedTarget)) close(false);
      });
    });
  }

  // The skip link's #MainContent href is a misnomer in Stencil: header-group
  // and footer-group render INSIDE <main> so autospacing's sibling rules see
  // one uniform .shopify-section flow, meaning the anchor alone lands on the
  // header. Intercept and focus the first section that isn't part of the
  // header group instead (no anchor element can be inserted between the
  // groups without breaking .shopify-section adjacency). The href stays as
  // the no-JS fallback.
  setupSkipLink() {
    const skip = document.querySelector('.skip-to-content-link');
    if (!skip) return;

    skip.addEventListener('click', (event) => {
      const target = document.querySelector(
        '#MainContent > .shopify-section:not(.shopify-section-group-header-group)'
      );
      if (!target) return;

      event.preventDefault();
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
      target.scrollIntoView();
    });
  }

  // Slide-in panels for the offcanvas nav's "slide" presentation
  // (snippets/nav-offcanvas.liquid): a trigger shifts the main panel out and
  // slides its sub-panel in; Back reverses it, returning focus to the trigger.
  // Delegated so any offcanvas rendering .nav-offcanvas-panels gets it.
  // Focus moves MUST preventScroll: the panels container clips with
  // overflow:hidden, which is still a programmatic scroll container, so a
  // plain focus() mid-transition horizontally scrolls it to "reveal" the
  // focused element and drags the stacked off-screen panels into view.
  // Hidden panels are inert for the same reason (and for tab order).
  setupOffcanvasPanels() {
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('.nav-offcanvas-trigger');
      if (trigger) {
        const container = trigger.closest('.nav-offcanvas-panels');
        const panel = document.getElementById(trigger.dataset.target);
        if (!container || !panel) return;
        panel.inert = false;
        panel.classList.add('active');
        container.querySelector('.nav-offcanvas-panel-main')?.classList.add('shifted');
        container.scrollTo(0, 0);
        panel.querySelector('.nav-offcanvas-back')?.focus({ preventScroll: true });
        return;
      }

      const back = event.target.closest('.nav-offcanvas-back');
      if (back) {
        const container = back.closest('.nav-offcanvas-panels');
        const panel = back.closest('.nav-offcanvas-panel-sub');
        if (!container || !panel) return;
        panel.classList.remove('active');
        panel.inert = true;
        container.querySelector('.nav-offcanvas-panel-main')?.classList.remove('shifted');
        container.scrollTo(0, 0);
        container.querySelector(`[data-target="${panel.id}"]`)?.focus({ preventScroll: true });
      }
    });

    // `close` does not bubble, so capture.
    document.addEventListener('close', (event) => {
      const container = event.target.querySelector('.nav-offcanvas-panels');
      if (!container) return;
      container.querySelectorAll('.nav-offcanvas-panel-sub.active').forEach((panel) => {
        panel.classList.remove('active');
        panel.inert = true;
      });
      container.querySelector('.nav-offcanvas-panel-main')?.classList.remove('shifted');
      container.scrollTo(0, 0);
    }, true);
  }

  // Away-from-top flag for the overlay header (custom.scss `.header-overlay`).
  // The scrolled-up / scrolled-down pair below is DIRECTIONAL — it says which
  // way the last gesture went, not whether the page has left the top — so it
  // cannot tell "resting over the hero, still transparent" from "scrolled back
  // up to the middle of the page, still needs its background". This can.
  // Set from inside the one scroll listener rather than a second one.
  #setScrolled(scrollTop) {
    if (!this.#section) return;
    this.#section.classList.toggle('header-scrolled', scrollTop > 4);
    this.#publishHeaderBottom();
  }

  setupScrollListener() {
    let lastScrollTop = window.pageYOffset || document.documentElement.scrollTop;
    this.#setScrolled(lastScrollTop);

    window.addEventListener("scroll", (e) => {
      let st = window.pageYOffset || document.documentElement.scrollTop; // Credits: "https://github.com/qeremy/so/blob/master/so.dom.js#L426"
      let difference = Math.abs(st - lastScrollTop);
      this.#setScrolled(st);
      // Add a 'tolerance' to prevent microscrolls from retriggering (e.g. on touch devices, there's usually a very small scroll up after scrolling down)
      if (st > lastScrollTop){
        let event = new CustomEvent("interaction:down-scroll", {
          detail: {
            difference: difference
          }
        });
        document.dispatchEvent(event);
      } else {
        let event = new CustomEvent("interaction:up-scroll", {
          detail: {
            difference: difference
          }
        });
        document.dispatchEvent(event);
      }
      lastScrollTop = st <= 0 ? 0 : st; // For Mobile or negative scrolling
    }, false);
  }
}

export default Nav;